import api from '../../services/api';
import { ParticipantResponse, ApiErrorResponse, AddParticipantPayload, EditParticipantPayload } from '../../types'; // Ensure these types are defined
import Cookies from 'js-cookie';

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000/api';

interface FetchParticipantsResponse {
  data: ParticipantResponse[];
  total: number;
  page: number;
  lastPage: number;
}

/**
 * Function to fetch all participants.
 * @returns An array of ParticipantResponse objects.
 */
export const fetchParticipants = async (token: string, estateId: string, urlParams: string = ''): Promise<ParticipantResponse[]> => {
  try {
    const response = await api.get<FetchParticipantsResponse>(`/participants/${estateId}?${urlParams}`);
    return response.data.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to fetch participants');
    } else {
      throw new Error('Failed to fetch participants');
    }
  }
};

/**
 * Function to add a new participant to an estate.
 * @param estateId - The UUID of the estate.
 * @param participantData - The participant data to add.
 * @param token - The authentication token.
 * @returns The created participant.
 */
export const addParticipant = async (
  estateId: string,
  participantData: AddParticipantPayload,
  token: string
): Promise<AddParticipantPayload> => {
  try {
    const response = await api.post<AddParticipantPayload>(`/participants/${estateId}`, participantData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to add participant');
    } else {
      throw new Error('Failed to add participant');
    }
  }
};

/**
 * Function to fetch details of a specific participant.
 * @param estateId - The UUID of the estate.
 * @param participantId - The ID of the participant.
 * @param token - The authentication token.
 * @returns The participant's details.
 */
export const getParticipant = async (
  estateId: string,
  participantId: string,
  token: string
): Promise<ParticipantResponse> => {
  try {
    const response = await api.get<ParticipantResponse>(`/participants/${estateId}/${participantId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to fetch participant details');
    } else {
      throw new Error('Failed to fetch participant details');
    }
  }
};

/**
 * Function to edit an existing participant.
 * @param participantId - The ID of the participant to edit.
 * @param participantData - The participant data to update.
 * @param token - The authentication token.
 * @returns The updated participant.
 */
export const editParticipant = async (
  estateId: string,
  participantId: string,
  participantData: EditParticipantPayload,
  token: string
): Promise<ParticipantResponse> => {
  try {
    const response = await api.patch<ParticipantResponse>(`/participants/${estateId}/${participantId}`, participantData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to edit participant');
    } else {
      throw new Error('Failed to edit participant');
    }
  }
};

/**
 * Function to delete a participant from an estate.
 * @param estateId - The UUID of the estate.
 * @param participantId - The ID of the participant to delete.
 * @param token - The authentication token.
 * @returns A success message or status.
 */
export const deleteParticipant = async (
  estateId: string,
  participantId: string,
  token: string
): Promise<{ message: string }> => {
  try {
    const response = await api.delete<{ message: string }>(`/participants/${estateId}/${participantId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to delete participant');
    } else {
      throw new Error('Failed to delete participant');
    }
  }
};

export const getRecentParticipants = async (
  estateId: string,
  count: number = 5
): Promise<ParticipantResponse[]> => {
  const response = await api.get<ParticipantResponse[]>(`/participants/${estateId}/recent/${count}`);
  return response.data;
};